import { useEffect, useState } from 'react';
import { Mic, Loader2, Download, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, ttsApi } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { useQuotaUpdate } from '../hooks/useQuotaUpdate';

const VoiceDesign = () => {
  const { t } = useTranslation('voiceDesign');
  const { user } = useAuthStore();
  const { refreshQuota } = useQuotaUpdate();
  const noQuota = !user || user.remaining <= 0;
  const [text, setText] = useState('');
  const [voiceDescription, setVoiceDescription] = useState('');
  const [cfgValue, setCfgValue] = useState(2.0);
  const [inferenceSteps, setInferenceSteps] = useState(10);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!loading) {
      setElapsedSeconds(0);
      return;
    }

    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setAudioUrl(null);

    try {
      const formData = new FormData();
      formData.append('text', text);
      formData.append('voice_description', voiceDescription);
      formData.append('cfg_value', cfgValue.toString());
      formData.append('inference_timesteps', inferenceSteps.toString());

      const response = await ttsApi.voiceDesign(formData);

      // 创建音频 URL
      const blob = new Blob([response.data], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      refreshQuota();
    } catch (err: any) {
      setError(await extractApiErrorMessage(err, t('error')));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `voice_design_${Date.now()}.wav`;
      link.click();
    }
  };

  const examples = [
    '年轻女性，温柔甜美的声音，语速适中',
    '中年男性，沉稳有力的声音，略带磁性',
    '老年女性，慈祥温和的声音，语速较慢',
    '年轻男性，活泼开朗的声音，充满朝气',
    '粤语，中年男性，语气平淡',
    '四川话，年轻女性，活泼可爱',
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600 mt-1">
          {t('description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 表单区域 */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="card space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {noQuota && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{t('error.quotaExceeded', { defaultValue: '今日配额已用完，请明天再试或升级 VIP' })}</span>
              </div>
            )}

            {loading && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                <p className="font-medium">{t('loading.title')}</p>
                <p className="text-sm mt-1">
                  {t('loading.waitTime', { seconds: elapsedSeconds })}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.textLabel')}
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="input h-32 resize-none"
                placeholder={t('form.textPlaceholder')}
                required
                maxLength={5000}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {t('form.textCounter', { count: text.length })}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('form.voiceDescLabel')}
              </label>
              <textarea
                value={voiceDescription}
                onChange={(e) => setVoiceDescription(e.target.value)}
                className="input h-24 resize-none"
                placeholder={t('form.voiceDescPlaceholder')}
                required
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1 text-right">
                {t('form.voiceDescCounter', { count: voiceDescription.length })}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.cfgLabel', { value: cfgValue })}
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={cfgValue}
                  onChange={(e) => setCfgValue(parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">{t('form.cfgHint')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.stepsLabel', { value: inferenceSteps })}
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  step="1"
                  value={inferenceSteps}
                  onChange={(e) => setInferenceSteps(parseInt(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">{t('form.stepsHint')}</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || noQuota}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('form.loading')}
                </>
              ) : (
                <>
                  <Mic className="w-5 h-5" />
                  {t('form.submit')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* 侧边栏 */}
        <div className="space-y-6">
          {/* 示例 */}
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">{t('examplesTitle')}</h3>
            <div className="space-y-2">
              {examples.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setVoiceDescription(example)}
                  className="w-full text-left p-3 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          {/* 结果 */}
          {audioUrl && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-4">{t('result.title')}</h3>
              <audio controls className="w-full mb-4">
                <source src={audioUrl} type="audio/wav" />
                {t('result.browserNotSupport')}
              </audio>
              <button
                onClick={handleDownload}
                className="w-full btn-secondary flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t('result.download')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceDesign;
